class ProductForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.variantIdInput.disabled = false;
    this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
    this.cartNotification = document.querySelector('cart-notification');
    this.cartItems = document.querySelector('cart-items');
    this.cartMain = document.querySelector('cart-main');
    this.cartFooter = document.getElementById('cart-footer');
    this.siteHeader = document.querySelector('.section--site-header');
    this.cartDrawer = document.getElementById('cart-drawer');
    this.cartDrawerOpener = document.getElementById('cart-drawer-opener');
    this.submitButton = this.querySelector('[type="submit"]');
    this.submitButtonText = this.submitButton.querySelector('span');

    this.hideErrors = this.dataset.hideErrors === 'true';
  }

  onSubmitHandler(event) {
    event.preventDefault();

    if (this.submitButton.classList.contains('loading')) return;

    // Enforce required line item properties (e.g. personalised card message).
    // Only bites when a field opts in via [data-required-property]; other products are unaffected.
    const requiredProperty = this.form.querySelector('[data-required-property]');
    if (requiredProperty && requiredProperty.value.trim() === '') {
      requiredProperty.setAttribute('aria-invalid', 'true');
      const describedBy = requiredProperty.getAttribute('aria-describedby') || '';
      describedBy.split(' ').forEach((id) => {
        const el = id && document.getElementById(id);
        if (el && el.hasAttribute('data-personalisation-error')) el.hidden = false;
      });
      requiredProperty.focus();
      requiredProperty.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Greeting cards are one per order. The basket can change without a page load
    // (drawer adds, another tab), so ask the live cart at submit time, then resubmit.
    if (requiredProperty && requiredProperty.hasAttribute('data-one-per-order')) {
      if (!this.cardLimitPassed) {
        this.checkCardLimit(requiredProperty).then((ok) => {
          if (!ok) return;
          this.cardLimitPassed = true;
          this.onSubmitHandler(event);
        });
        return;
      }
      this.cardLimitPassed = false;
    }

    this.handleErrorMessage();

    this.submitButton.setAttribute('aria-disabled', true);
    this.submitButton.classList.add('loading');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData(this.form);

    // Prep main cart sections for updates on the cart page no matter the cart type
    if (themeSettings.template_name === 'cart') {
      formData.append('sections', this.cartMain.getSectionsToRender().map((section) => section.id));
      formData.append('sections_url', window.location.pathname);
      config.body = formData;
    // Prep cart notification for updates
    } else if (themeSettings.cart_type === 'page') {
      formData.append('sections', this.cartNotification.getSectionsToRender().map((section) => section.id));
      formData.append('sections_url', window.location.pathname);
      config.body = formData;
    // Prep cart drawer for updates
    } else {
      formData.append('sections', this.cartDrawer.getSectionsToRender().map((section) => section.id));
      formData.append('sections_url', window.location.pathname);
      config.body = formData;
    }

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            errors: response.errors || response.description,
            message: response.message,
          });

          this.handleErrorMessage(response.description);
          const soldOutMessage = this.submitButton.querySelector('.js-sold-out-message');
          if (!soldOutMessage) return;
          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButtonText.classList.add('display--hidden');
          soldOutMessage.classList.remove('display--hidden');
          this.error = true;
          return;
        }

        document.querySelectorAll('simple-modal-dialog').forEach((dialog) => {
          dialog.hide();
        });

        const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');

        if (!this.error) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            cartData: response,
          }).then(() => {
            CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
          });
          // Main cart sections renders updates on the cart page no matter the cart type
          if (themeSettings.template_name === 'cart') {
            this.cartItems.querySelector('#cart-items-loader').classList.add('display--hidden');
            this.cartMain.getSectionsToRender().forEach((section => {
              document.getElementById(section.id).innerHTML =
                this.getSectionInnerHTML(response.sections[section.id], section.selector);
            }));
            this.cartItems.hideEmptyCart();
            window.scrollTo({top: 0, behavior: 'smooth'});
          // Cart notification renders updates
          } else if (themeSettings.cart_type === 'page') {
            this.cartNotification.setActiveElement(document.activeElement);
            this.cartNotification.renderContents(response);
            this.cartNotification.open();
          // Cart drawer renders updates
          } else {
            this.cartItems.querySelector('#cart-items-loader').classList.remove('display--hidden');
            this.cartDrawerOpener.openDrawer('cart-drawer');
            this.cartDrawer.setActiveElement(document.activeElement);
            this.cartItems.querySelector('#cart-items-loader').classList.add('display--hidden');
            this.cartDrawer.getSectionsToRender().forEach((section => {
              document.getElementById(section.id).innerHTML =
                this.getSectionInnerHTML(response.sections[section.id], section.selector);
            }));
            this.cartItems.hideEmptyCart();
            trapFocus(this.cartDrawer);
          }
        }
        this.error = false;
      })
      .catch((e) => {
        console.error(e);
        if (!this.cartItems) return;
        this.cartItems.querySelector('#cart-items-loader').classList.remove('display--hidden');
      })
      .finally(() => {
        this.submitButton.classList.remove('loading');
        this.submitButton.removeAttribute('aria-disabled');
        CartPerformance.measureFromEvent("add:user-action", event);
      });
  }

  // Resolves false (and shows the notice) when the basket already holds a card,
  // recognised by its "Card message" property. A failed lookup never blocks a sale.
  checkCardLimit(field) {
    const notice = (field.getAttribute('aria-describedby') || '')
      .split(' ')
      .map((id) => id && document.getElementById(id))
      .find((el) => el && el.hasAttribute('data-personalisation-limit'));

    return fetch(`${routes.cart_add_url.replace(/\/add(\.js)?$/, '')}.js`)
      .then((response) => response.json())
      .then((cart) => {
        const hasCard = cart.items.some((item) => item.properties && item.properties['Card message']);
        if (notice) notice.hidden = !hasCard;
        return !hasCard;
      })
      .catch(() => true);
  }

  handleErrorMessage(errorMessage = false) {
    if (this.hideErrors) return;

    this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('[data-error-message-wrapper]');
    this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('[data-error-message]');

    this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

    if (errorMessage) {
      this.errorMessage.textContent = errorMessage;
    }
  }

  toggleSubmitButton(disable = true, text) {
    if (disable) {
      this.submitButton.setAttribute('disabled', 'disabled');
      if (text) this.submitButtonText.textContent = text;
    } else {
      this.submitButton.removeAttribute('disabled');
      this.submitButtonText.textContent = window.variantStrings.addToCart;
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  get variantIdInput() {
    return this.form.querySelector('[name=id]');
  }
}

customElements.define('product-form', ProductForm);
